import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MetricsBento } from "./MetricsBento";
import { GrowthMiniChart } from "./GrowthMiniChart";
import { ChannelBreakdownBars } from "./ChannelBreakdownBars";
import { TopReferrersMini } from "./TopReferrersMini";

/**
 * Skeleton mimicking the bento so the layout doesn't jump on first paint.
 */
function BentoSkeleton() {
  return (
    <div
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:grid-rows-2"
      data-testid="admin-metrics-loading-skeleton"
    >
      <div className="lg:col-span-2 lg:row-span-2 h-64 animate-pulse rounded-2xl border border-gray-100 bg-gray-50" />
      <div className="h-40 animate-pulse rounded-2xl border border-gray-100 bg-gray-50" />
      <div className="h-40 animate-pulse rounded-2xl border border-gray-100 bg-gray-50" />
      <div className="h-40 animate-pulse rounded-2xl border border-gray-100 bg-gray-50" />
      <div className="h-40 animate-pulse rounded-2xl border border-gray-100 bg-gray-50" />
    </div>
  );
}

/**
 * Command-center orchestrator — one metrics fetch, cascades to all children.
 * Listens for the `admin:metrics-refresh` window event so any admin action
 * (verify / reject / purge) can trigger a live refresh without prop drilling.
 */
export function AdminCommandCenter() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/metrics");
      setMetrics(data);
    } catch (err) {
      toast.error(formatApiError(err, "Failed to load admin metrics"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // React to admin mutations dispatched from the queue.
  useEffect(() => {
    const handler = () => load();
    window.addEventListener("admin:metrics-refresh", handler);
    return () => window.removeEventListener("admin:metrics-refresh", handler);
  }, [load]);

  if (loading && !metrics) {
    return (
      <div className="space-y-6" data-testid="admin-command-center-loading">
        <BentoSkeleton />
        <div className="h-64 animate-pulse rounded-2xl border border-gray-100 bg-gray-50" />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="h-72 animate-pulse rounded-2xl border border-gray-100 bg-gray-50" />
          <div className="h-72 animate-pulse rounded-2xl border border-gray-100 bg-gray-50" />
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  return (
    <div className="mb-rise space-y-6" data-testid="admin-command-center">
      <MetricsBento metrics={metrics} />
      <GrowthMiniChart series={metrics.growth?.daily_signups} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        <div className="lg:col-span-3">
          <ChannelBreakdownBars breakdown={metrics.referrals?.channel_breakdown} />
        </div>
        <div className="lg:col-span-2">
          <TopReferrersMini referrers={metrics.referrals?.top_referrers} />
        </div>
      </div>
      {loading && (
        <div className="flex justify-center pt-1 text-[11px] text-gray-400">
          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          Refreshing…
        </div>
      )}
    </div>
  );
}
